<template>
  <div class="auditor-assign-page">
    <!-- 顶部返回 + 标题 -->
    <div class="page-header">
      <button class="back-btn" @click="goBack">
        <i class="fas fa-arrow-left"></i>
        <span>{{ $t('controlCenter.auditorSeatManagement.assignPage.backToList') }}</span>
      </button>
      <div class="page-title-block">
        <h1>{{ $t('controlCenter.auditorSeatManagement.assignPage.title') }}</h1>
        <p class="subtitle">{{ $t('controlCenter.auditorSeatManagement.assignPage.subtitle') }}</p>
      </div>
    </div>

    <!-- 席位信息卡 -->
    <div v-loading="seatLoading" class="seat-info-card">
      <div v-if="seatLoadError" class="seat-error">
        <i class="fas fa-circle-exclamation"></i>
        <span>{{ $t('controlCenter.auditorSeatManagement.assignPage.seatNotFound') }}</span>
        <button class="btn btn-primary" @click="goBack">
          {{ $t('controlCenter.auditorSeatManagement.assignPage.backToList') }}
        </button>
      </div>
      <div v-else-if="seat" class="seat-info-grid">
        <div class="seat-info-item">
          <span class="seat-info-label">{{ $t('controlCenter.auditorSeatManagement.assignPage.seatNameLabel') }}</span>
          <span class="seat-info-value">{{ seat.name || seat.id }}</span>
        </div>
        <div class="seat-info-item">
          <span class="seat-info-label">{{ $t('controlCenter.auditorSeatManagement.assignPage.seatStatusLabel') }}</span>
          <span class="seat-info-value">
            <el-tag :type="seat.assignedAuditor ? 'success' : 'info'" size="small">
              {{ seat.assignedAuditor
                ? $t('controlCenter.auditorSeatManagement.assignPage.seatAssigned')
                : $t('controlCenter.auditorSeatManagement.assignPage.seatUnassigned') }}
            </el-tag>
          </span>
        </div>
        <div v-if="seat.assignedAuditor" class="seat-info-item">
          <span class="seat-info-label">{{ $t('controlCenter.auditorSeatManagement.dialogs.assign.auditorLabel') }}</span>
          <span class="seat-info-value">
            {{ seat.assignedAuditor.name || seat.assignedAuditor.email || seat.assignedAuditor.id }}
          </span>
        </div>
      </div>
      <div v-else class="seat-info-placeholder">
        {{ $t('controlCenter.auditorSeatManagement.assignPage.loadingSeat') }}
      </div>
    </div>

    <!-- 用户选择区 -->
    <div ref="selectSection" class="select-section">
      <!-- 工具栏 -->
      <div ref="toolbar" class="assign-toolbar">
        <el-input
          v-model="form.keyword"
          size="small"
          clearable
          :placeholder="$t('controlCenter.auditorSeatManagement.dialogs.assign.searchPlaceholder')"
          prefix-icon="el-icon-search"
          class="assign-search"
        />
        <el-checkbox v-model="form.showUnassignable" class="assign-toggle">
          {{ $t('controlCenter.auditorSeatManagement.dialogs.assign.showUnassignableLabel') }}
        </el-checkbox>
        <span class="assign-count">
          {{ auditorOptions.length }} / {{ auditorRawList.length }}
          {{ $t('controlCenter.auditorSeatManagement.dialogs.assign.countSuffix') }}
        </span>
      </div>

      <!-- 用户表 -->
      <el-table
        v-loading="auditorLoading"
        :data="auditorOptions"
        size="small"
        class="assign-table"
        :row-class-name="getRowClassName"
        @row-click="handleRowClick"
      >
        <el-table-column width="48" align="center" class-name="col-radio">
          <template slot-scope="{ row }">
            <el-radio
              :value="form.auditorId"
              :label="row.id"
              :disabled="isUnassignable(row.raw)"
              @change="handleSelectAuditor(row.id)"
            >
              <span></span>
            </el-radio>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('controlCenter.auditorSeatManagement.fields.auditUserType')"
          width="170"
          align="center"
          sortable
          :sort-method="sortByAuditUserType"
        >
          <template slot-scope="{ row }">
            <span
              class="copyable-cell"
              @dblclick="copyCell($event, row.raw.auditUserType ? getAuditUserTypeLabel(row.raw.auditUserType) : '')"
            >
              <el-tag
                v-if="row.raw.auditUserType"
                :type="getAuditUserTypeTagType(row.raw.auditUserType)"
                size="mini"
                effect="plain"
              >
                {{ getAuditUserTypeLabel(row.raw.auditUserType) }}
              </el-tag>
              <span v-else class="not-audit-user-text">
                {{ $t('controlCenter.auditorSeatManagement.fields.notAuditUserPlaceholder') }}
              </span>
            </span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('controlCenter.auditorSeatManagement.fields.auditUserNumeral')"
          width="180"
          align="center"
          sortable
          :sort-method="sortByAuditUserNumeral"
        >
          <template slot-scope="{ row }">
            <span
              class="copyable-cell"
              @dblclick="copyCell($event, row.raw.auditUserNumeral)"
            >
              {{ row.raw.auditUserNumeral || $t('controlCenter.auditorSeatManagement.auditUserTypes.placeholder') }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('controlCenter.auditorSeatManagement.fields.hasSeat')"
          min-width="130"
          align="center"
          sortable
          :sort-method="sortByHasSeat"
        >
          <template slot-scope="{ row }">
            <el-tooltip
              :content="row.raw.hasSeat
                ? $t('controlCenter.auditorSeatManagement.fields.hasSeatTooltipTrue')
                : $t('controlCenter.auditorSeatManagement.fields.hasSeatTooltipFalse')"
              placement="top"
              effect="light"
            >
              <svg
                class="seat-icon"
                :class="row.raw.hasSeat ? 'seat-occupied' : 'seat-empty'"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M19 9h-1V6c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v3H5c-1.1 0-2 .9-2 2v6h2v-2h14v2h2v-6c0-1.1-.9-2-2-2zm-3 0H8V6h8v3zM7 18h2v2h6v-2h2v3H7z"
                />
              </svg>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('controlCenter.auditorSeatManagement.fields.name')"
          min-width="150"
          show-overflow-tooltip
          sortable
          :sort-method="sortByName"
        >
          <template slot-scope="{ row }">
            <span
              class="copyable-cell"
              @dblclick="copyCell($event, getFullName(row.raw) || row.label)"
            >{{ getFullName(row.raw) || row.label }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('controlCenter.auditorSeatManagement.fields.email')"
          min-width="200"
          show-overflow-tooltip
          sortable
          :sort-method="sortByEmail"
        >
          <template slot-scope="{ row }">
            <span
              class="copyable-cell"
              @dblclick="copyCell($event, row.raw.email)"
            >{{ row.raw.email }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('controlCenter.auditorSeatManagement.fields.lastLoginTime')"
          width="180"
          sortable
          :sort-method="sortByLastLoginTime"
        >
          <template slot-scope="{ row }">
            <span
              class="copyable-cell"
              @dblclick="copyCell($event, formatTime(row.raw.lastLoginTime))"
            >{{ formatTime(row.raw.lastLoginTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('controlCenter.auditorSeatManagement.dialogs.assign.assignableTag')"
          width="120"
          align="center"
          fixed="right"
          sortable
          :sort-method="sortByAssignable"
        >
          <template slot-scope="{ row }">
            <el-tooltip
              :content="isUnassignable(row.raw)
                ? $t('controlCenter.auditorSeatManagement.dialogs.assign.unassignableTag')
                : $t('controlCenter.auditorSeatManagement.dialogs.assign.assignableTag')"
              placement="top"
              effect="light"
            >
              <svg
                v-if="isUnassignable(row.raw)"
                class="status-icon status-unassignable"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden="true"
              >
                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.93 1.69L5.69 16.93C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.93-1.69L18.31 7.07C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z" />
              </svg>
              <svg
                v-else
                class="status-icon status-assignable"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden="true"
              >
                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </el-tooltip>
          </template>
        </el-table-column>

        <template slot="empty">
          <div class="table-empty">
            <i class="fas fa-folder-open"></i>
            <p>{{ $t('controlCenter.auditorSeatManagement.dialogs.assign.empty') }}</p>
          </div>
        </template>
      </el-table>
    </div>

    <!-- 选中信息区 -->
    <div v-if="selectedAuditor" class="selected-info">
      <!-- hasSeat=true 替换席位提示 -->
      <div v-if="selectedAuditor.hasSeat === true" class="replace-box">
        <div class="replace-header">
          <i class="fas fa-triangle-exclamation replace-icon"></i>
          <span class="replace-title">{{ $t('controlCenter.auditorSeatManagement.dialogs.assign.seatReplaceWarning') }}</span>
        </div>
      </div>

      <!-- isAuditUser=false 自动注册提示 + 勾选 -->
      <div v-if="selectedAuditor.isAuditUser === false" class="info-box">
        <div class="info-header">
          <i class="fas fa-circle-info info-icon"></i>
          <span class="info-title">{{ $t('controlCenter.auditorSeatManagement.dialogs.assign.notAuditUserInfo') }}</span>
        </div>
        <el-checkbox v-model="form.autoRegister" class="auto-register-checkbox">
          {{ $t('controlCenter.auditorSeatManagement.dialogs.assign.autoRegisterLabel') }}
        </el-checkbox>
      </div>
    </div>

    <!-- 底部固定操作栏 -->
    <div class="action-bar">
      <button class="btn" @click="goBack">
        {{ $t('controlCenter.auditorSeatManagement.assignPage.cancel') }}
      </button>
      <button class="btn btn-primary" :disabled="confirmDisabled || actionLoading" @click="openConfirmDialog">
        <i v-if="actionLoading" class="fas fa-circle-notch fa-spin"></i>
        {{ $t('controlCenter.auditorSeatManagement.assignPage.confirmAssign') }}
      </button>
    </div>

    <!-- 确认对话框 -->
    <el-dialog
      :title="$t('controlCenter.auditorSeatManagement.dialogs.assign.warningTitle')"
      :visible.sync="confirmDialogVisible"
      width="520px"
      custom-class="auditor-confirm-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="confirm-dialog-body">
        <div class="confirm-icon-row">
          <i class="fas fa-triangle-exclamation confirm-icon"></i>
          <span class="confirm-title">{{ $t('controlCenter.auditorSeatManagement.dialogs.assign.warningTitle') }}</span>
        </div>
        <p class="confirm-message">{{ $t('controlCenter.auditorSeatManagement.dialogs.assign.warningMessage') }}</p>
        <el-checkbox v-model="form.acknowledged" class="acknowledge-checkbox">
          {{ $t('controlCenter.auditorSeatManagement.dialogs.assign.acknowledgeLabel') }}
        </el-checkbox>
      </div>
      <span slot="footer" class="dialog-footer">
        <button class="btn" @click="confirmDialogVisible = false">
          {{ $t('controlCenter.auditorSeatManagement.assignPage.cancel') }}
        </button>
        <button class="btn btn-primary" :disabled="!form.acknowledged || actionLoading" @click="confirmAssign">
          <i v-if="actionLoading" class="fas fa-circle-notch fa-spin"></i>
          {{ $t('controlCenter.auditorSeatManagement.assignPage.confirmAssign') }}
        </button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { iamApi } from '@/api/iam'
import { getUserInfo } from '@/utils/auth'

export default {
  name: 'AuditorSeatAssign',
  data() {
    return {
      seatLoading: false,
      seatLoadError: false,
      seat: null,
      auditorRawList: [],
      auditorLoading: false,
      actionLoading: false,
      confirmDialogVisible: false,
      form: {
        auditorId: '',
        acknowledged: false,
        showUnassignable: false,
        autoRegister: true,
        keyword: ''
      }
    }
  },
  computed: {
    accountId() {
      const userInfo = getUserInfo()
      return userInfo?.accountId || userInfo?.sub || userInfo?.userId || 'default'
    },
    seatId() {
      return this.$route.query.seatId || ''
    },
    preselectedIdentityId() {
      return this.$route.query.identityId || ''
    },
    auditorOptions() {
      const { showUnassignable, keyword } = this.form
      const kw = (keyword || '').trim().toLowerCase()
      return this.auditorRawList
        .filter(u => {
          if (showUnassignable) return true
          return !this.isUnassignable(u)
        })
        .filter(u => {
          if (!kw) return true
          const name = this.getFullName(u).toLowerCase()
          const email = (u.email || '').toLowerCase()
          const numeral = (u.auditUserNumeral || '').toLowerCase()
          return name.includes(kw) || email.includes(kw) || numeral.includes(kw)
        })
        .map(u => this.buildOption(u))
    },
    selectedAuditor() {
      if (!this.form.auditorId) return null
      return this.auditorRawList.find(u => u.id === this.form.auditorId) || null
    },
    confirmDisabled() {
      if (!this.form.auditorId) return true
      if (this.selectedAuditor && this.selectedAuditor.isAuditUser === false && !this.form.autoRegister) {
        return true
      }
      return false
    }
  },
  created() {
    if (!this.seatId) {
      this.seatLoadError = true
      return
    }
    this.fetchSeatAndAuditors()
  },
  methods: {
    async fetchSeatAndAuditors() {
      this.seatLoading = true
      this.auditorLoading = true
      try {
        const subscription = await iamApi.getSubscription(this.accountId)
        const seats = (subscription && subscription.auditorSeats) || []
        const target = seats.find(s => s.id === this.seatId)
        if (!target) {
          this.seatLoadError = true
          this.seat = null
        } else {
          this.seat = target
        }
      } catch (error) {
        console.error('Failed to fetch seat:', error)
        const errorMsg = error?.response?.data?.message || error?.response?.data?.detail
        if (errorMsg) {
          this.$message.error(errorMsg)
        }
        this.seatLoadError = true
      } finally {
        this.seatLoading = false
      }

      try {
        const response = await iamApi.getIdentities(this.accountId)
        const identities = Array.isArray(response) ? response : (response?.content || response?.data || [])
        this.auditorRawList = identities
        // 路由带 identityId 时自动选中该用户
        if (this.preselectedIdentityId && !this.form.auditorId) {
          const matched = identities.find(u => u.id === this.preselectedIdentityId)
          if (matched) {
            this.form.auditorId = matched.id
            // 不可分配用户时，将“显示不可分配的用户”开关打开以便选中可见
            if (this.isUnassignable(matched)) {
              this.form.showUnassignable = true
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch auditors:', error)
        const errorMsg = error?.response?.data?.message || error?.response?.data?.detail
        if (errorMsg) {
          this.$message.error(errorMsg)
        }
        this.auditorRawList = []
      } finally {
        this.auditorLoading = false
      }
    },
    isUnassignable(u) {
      return !!(u && u.isAuditUser === true && u.auditUserType && u.auditUserType !== 'AUDITOR')
    },
    buildOption(u) {
      const name = this.getFullName(u)
      return {
        id: u.id,
        label: name || u.email || u.id,
        disabled: this.isUnassignable(u),
        raw: u
      }
    },
    getFullName(u) {
      const fullName = (u && u.fullName) || {}
      return `${fullName.firstName || ''} ${fullName.lastName || ''}`.trim()
    },
    getAuditUserTypeLabel(type) {
      if (!type) return this.$t('controlCenter.auditorSeatManagement.auditUserTypes.placeholder')
      return this.$t(`controlCenter.auditorSeatManagement.auditUserTypes.${type}`) || type
    },
    getAuditUserTypeTagType(type) {
      if (type === 'AUDITOR') return 'success'
      if (!type) return 'info'
      return 'danger'
    },
    // 列排序比较函数（Element UI sort-method 约定：返回 <0 表示 a 在前）：
    // 字符串类：空值统一用 'ZZZ' 占位使其在升序时排到末尾
    sortByAuditUserType(a, b) {
      const vA = a.raw.auditUserType || 'ZZZ'
      const vB = b.raw.auditUserType || 'ZZZ'
      return vA.localeCompare(vB)
    },
    sortByAuditUserNumeral(a, b) {
      const vA = a.raw.auditUserNumeral || 'ZZZ'
      const vB = b.raw.auditUserNumeral || 'ZZZ'
      return vA.localeCompare(vB)
    },
    // hasSeat：true > false（升序时 false 在前）
    sortByHasSeat(a, b) {
      return (a.raw.hasSeat ? 1 : 0) - (b.raw.hasSeat ? 1 : 0)
    },
    sortByName(a, b) {
      const vA = this.getFullName(a.raw).toLowerCase()
      const vB = this.getFullName(b.raw).toLowerCase()
      return vA.localeCompare(vB)
    },
    sortByEmail(a, b) {
      return (a.raw.email || '').localeCompare(b.raw.email || '')
    },
    sortByLastLoginTime(a, b) {
      const tA = a.raw.lastLoginTime ? new Date(a.raw.lastLoginTime).getTime() : 0
      const tB = b.raw.lastLoginTime ? new Date(b.raw.lastLoginTime).getTime() : 0
      return tA - tB
    },
    // 可分配状态：可分配(false) < 不可分配(true)，升序时可分配在前
    sortByAssignable(a, b) {
      return (this.isUnassignable(a.raw) ? 1 : 0) - (this.isUnassignable(b.raw) ? 1 : 0)
    },
    formatTime(t) {
      if (!t) return this.$t('controlCenter.auditorSeatManagement.auditUserTypes.placeholder')
      try {
        const d = new Date(t)
        if (Number.isNaN(d.getTime())) return t
        return d.toLocaleString()
      } catch (_) {
        return t
      }
    },
    handleSelectAuditor(id) {
      const u = this.auditorRawList.find(x => x.id === id)
      if (!u) return
      if (this.isUnassignable(u)) {
        this.$message.warning(this.$t('controlCenter.auditorSeatManagement.dialogs.assign.nonAuditorBlockTip'))
        return
      }
      // 再次点击已选中行 → 取消选中
      if (this.form.auditorId === id) {
        this.form.auditorId = ''
        this.form.acknowledged = false
        this.form.autoRegister = false
        return
      }
      this.form.acknowledged = false
      this.form.autoRegister = false
      this.form.auditorId = id
    },
    handleRowClick(row) {
      if (!row) return
      this.handleSelectAuditor(row.id)
    },
    async copyCell(event, text) {
      if (!text) return
      // 用 Range 选中当前单元格的整个文本，覆盖浏览器默认按 word 选中
      try {
        const range = document.createRange()
        range.selectNodeContents(event.currentTarget)
        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(range)
      } catch (e) {
        // 选中失败时忽略，不影响复制
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          document.execCommand('copy')
        }
        this.$message.success(this.$t('controlCenter.auditorSeatManagement.messages.copied'))
      } catch (e) {
        this.$message.warning(this.$t('controlCenter.auditorSeatManagement.messages.copied'))
      }
    },
    getRowClassName({ row }) {
      const classes = []
      if (row && this.isUnassignable(row.raw)) classes.push('row-unassignable')
      if (row && this.form.auditorId === row.id) classes.push('row-selected')
      return classes.join(' ')
    },
    openConfirmDialog() {
      if (this.confirmDisabled || this.actionLoading) return
      // 每次打开都重置已了解状态，强制用户重新确认
      this.form.acknowledged = false
      this.confirmDialogVisible = true
    },
    async confirmAssign() {
      if (!this.form.acknowledged || this.actionLoading) return
      this.actionLoading = true
      try {
        const numeral = this.selectedAuditor ? this.selectedAuditor.auditUserNumeral || null : null
        const response = await iamApi.assignAuditor(this.accountId, this.seatId, this.form.auditorId, numeral, !!this.form.autoRegister)
        this.confirmDialogVisible = false
        const successMsg = (response && (response.message || response.detail)) || this.$t('controlCenter.auditorSeatManagement.messages.assignSuccess')
        this.$message.success(successMsg)
        this.goBack()
      } catch (error) {
        console.error('Failed to assign auditor:', error)
        const errorMsg = error?.response?.data?.message || error?.response?.data?.detail || this.$t('controlCenter.auditorSeatManagement.messages.assignError')
        this.$message.error(errorMsg)
      } finally {
        this.actionLoading = false
      }
    },
    goBack() {
      this.$router.push({
        path: '/controlCenter',
        query: { tab: 'users' }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.auditor-assign-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.page-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
  transition: all 0.2s;
  flex-shrink: 0;
}

.back-btn:hover {
  border-color: #2563eb;
  color: #2563eb;
}

.page-title-block {
  flex: 1;
  min-width: 0;
}

.page-title-block h1 {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.subtitle {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

/* 席位信息卡 */
.seat-info-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px 20px;
  min-height: 64px;
  display: flex;
  align-items: center;
}

.seat-info-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  width: 100%;
}

.seat-info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.seat-info-label {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
}

.seat-info-value {
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.seat-info-placeholder {
  color: #9ca3af;
  font-size: 13px;
}

.seat-error {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  color: #b91c1c;
  font-size: 13px;
}

.seat-error i {
  font-size: 18px;
}

.seat-error .btn {
  margin-left: auto;
}

/* 选择区 */
.select-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.assign-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.assign-search {
  flex: 1 1 320px;
  min-width: 260px;
}

.assign-toggle {
  font-size: 13px;
  color: #374151;
}

.assign-count {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.assign-table {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.assign-table ::v-deep .el-table__row {
  cursor: pointer;
}

.assign-table ::v-deep .el-table__row.row-unassignable {
  background-color: #fafafa;
  color: #9ca3af;
}

.assign-table ::v-deep .el-table__row.row-unassignable td {
  color: #9ca3af;
}

.assign-table ::v-deep .el-table__row.row-selected {
  background-color: #eff6ff;
}

.assign-table ::v-deep .el-table__row.row-selected td {
  color: #1e40af;
  font-weight: 500;
}

.assign-table ::v-deep .col-radio .cell {
  padding-left: 4px;
  padding-right: 4px;
  text-align: center;
}

.assign-table ::v-deep .el-radio__label {
  display: none;
}

/* 列头排序箭头：未激活时隐藏，hover 时显示，激活时始终显示
   注意：不限定 .el-table__header-wrapper，让样式同时作用于 fixed 列的复制表头 */
.assign-table ::v-deep th .caret-wrapper {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.assign-table ::v-deep th:hover .caret-wrapper {
  opacity: 1;
}

.assign-table ::v-deep th.ascending .caret-wrapper,
.assign-table ::v-deep th.descending .caret-wrapper {
  opacity: 1;
}

/* 列头内容强制不换行：title + 排序箭头保持在同一行 */
.assign-table ::v-deep th .cell {
  white-space: nowrap;
}

/* hasSeat 列图标（内联 SVG，不依赖 FontAwesome） */
.seat-icon {
  display: inline-block;
  vertical-align: middle;
  /* 不设置 cursor，使用默认 cursor 避免暗示可点击 */
  outline: none;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.seat-icon.seat-occupied {
  color: #f59e0b;
}

.seat-icon.seat-empty {
  color: #d1d5db;
}

/* Assignable 列图标（内联 SVG，不依赖 FontAwesome） */
.status-icon {
  display: inline-block;
  vertical-align: middle;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.status-icon.status-assignable {
  color: #059669;
}

.status-icon.status-unassignable {
  color: #dc2626;
}

/* Audit User Type 列：非 audit 用户的占位提示 */
.not-audit-user-text {
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
}
.copyable-cell {
  cursor: text;
  display: inline-block;
  max-width: 100%;
  border-radius: 2px;
  transition: background-color 0.15s ease;
}
.copyable-cell:hover {
  background-color: rgba(64, 158, 255, 0.08);
}

.table-empty {
  padding: 32px 0;
  color: #9ca3af;
  text-align: center;
}

.table-empty i {
  font-size: 32px;
  margin-bottom: 8px;
  display: block;
}

.table-empty p {
  margin: 0;
  font-size: 13px;
}

/* 选中信息区 */
.selected-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.replace-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
}

.replace-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.replace-icon {
  color: #dc2626;
  font-size: 15px;
  flex-shrink: 0;
}

.replace-title {
  font-size: 13px;
  font-weight: 600;
  color: #991b1b;
}

.info-box {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px 16px;
}

.info-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.info-icon {
  color: #2563eb;
  font-size: 15px;
  flex-shrink: 0;
  margin-top: 2px;
}

.info-title {
  font-size: 13px;
  color: #1e40af;
  line-height: 1.5;
}

.auto-register-checkbox {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #1e40af;
}

.auto-register-checkbox ::v-deep .el-checkbox__label {
  font-size: 13px;
  color: #1e40af;
}

/* 确认对话框 */
.confirm-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
}

.confirm-icon-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.confirm-icon {
  color: #ea580c;
  font-size: 22px;
  flex-shrink: 0;
}

.confirm-title {
  font-size: 16px;
  font-weight: 700;
  color: #c2410c;
}

.confirm-message {
  font-size: 13px;
  color: #4b5563;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

.acknowledge-checkbox {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #1f2937;
}

.acknowledge-checkbox ::v-deep .el-checkbox__label {
  font-size: 13px;
  color: #1f2937;
  font-weight: 500;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* 底部固定操作栏 */
.action-bar {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 20px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  backdrop-filter: blur(8px);
  box-shadow: 0 -4px 14px rgba(15, 23, 42, 0.05);
  z-index: 5;
}

.btn {
  padding: 8px 18px;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: all 0.2s;
}

.btn:hover:not(:disabled) {
  border-color: #9ca3af;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.btn-primary:hover:not(:disabled) {
  background: #1d4ed8;
}

@media screen and (max-width: 768px) {
  .seat-info-grid {
    gap: 16px;
  }
  .assign-toolbar {
    gap: 10px;
  }
  .action-bar {
    padding: 12px 14px;
  }
}
</style>
